import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React from 'react';
import { useAuthContext } from '../../../../../../context/auth';
import { errorToast, successToast } from '../../../../../Toast';
import { TenantsApi } from '../../../../namespace/api';
import {
	deleteWebhook,
	QUERY_KEYS,
	WebhookResponse,
} from '../../../api/webhooks';
import {
	CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_CANCEL_TEXT,
	CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_CONFIRM_MESSAGE,
	CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_DELETE_TEXT,
	CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_HEADER_LABEL,
	DELETE_WEBHOOK_CONFIGURATION_FAILURE,
	DELETE_WEBHOOK_CONFIGURATION_SUCCESS,
} from '../../../data/constants';
import { handleModalClose } from '../../../utils/handleModalClose';

interface Props {
	open: boolean;
	configuredWebhook: WebhookResponse;
	tenantHostname?: string;
	onCancel: () => void;
}

export default function ConfirmDeleteWebhookConfigurationModal({
	open,
	configuredWebhook,
	tenantHostname,
	onCancel,
}: Props) {
	const client = useQueryClient();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const authTenantId = authData?.tenantAuthorizationData?.tenantId;
	const { mutate: onDelete, isLoading } = useMutation(
		() => {
			return tenantHostname
				? TenantsApi.deleteWebhook(configuredWebhook.name, tenantHostname)
				: deleteWebhook(configuredWebhook.name, authToken);
		},
		{
			onSuccess: () => {
				client.invalidateQueries([
					QUERY_KEYS.WEBHOOK_CONFIGURATIONS,
					authTenantId,
				]);

				onCancel();
				successToast(DELETE_WEBHOOK_CONFIGURATION_SUCCESS());
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err?.response?.data?.message) {
					errorToast(
						DELETE_WEBHOOK_CONFIGURATION_FAILURE() +
							': ' +
							err.response.data.message,
					);
				} else {
					errorToast(DELETE_WEBHOOK_CONFIGURATION_FAILURE());
				}
			},
		},
	);

	return (
		<Dialog open={open} onClose={(e, r) => handleModalClose(r, onCancel)}>
			<DialogTitle>
				{CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_HEADER_LABEL()}
			</DialogTitle>
			<DialogContent>
				<Stack alignItems='flex-start' direction='column' spacing={0}>
					<Typography>
						{CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_CONFIRM_MESSAGE(
							configuredWebhook.name,
						)}
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ m: 2 }}>
				<Button onClick={onCancel} color='neutralButton'>
					{CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_CANCEL_TEXT()}
				</Button>
				<Button color='error' variant='contained' onClick={() => onDelete()}>
					{isLoading ? (
						<CircularProgress size={20} style={{ color: 'white' }} />
					) : (
						CONFIRM_DELETE_WEBHOOK_CONFIGURATION_MODAL_DELETE_TEXT()
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
